import { Box, CircularProgress, Typography } from "@mui/material";
import { useNotification } from "app/NotificationProvider";
import type { FileItem } from "common/components/FileItemList";
import FileItemList from "common/components/FileItemList";
import { useActionEvidence, useDeleteActionFileEntry } from "model";
import { useAppStore } from "stores";

interface Props {
  actionSubmissionGuid: string;
}

export default function ActionEvidenceList({ actionSubmissionGuid }: Props) {
  const { data: evidence = [], isLoading } =
    useActionEvidence(actionSubmissionGuid);
  const deleteActionFileEntry = useDeleteActionFileEntry();
  const { notify } = useNotification();
  const user = useAppStore((state) => state.user);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (evidence.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: "#71717a", fontSize: "14px" }}>
        No evidence uploaded yet.
      </Typography>
    );
  }

  const files: FileItem[] = evidence.map((item) => ({
    guid: item.guid,
    fileEntryGuid: item.fileEntryGuid,
    fileName: item.fileName,
    fileUploadType: item.fileUploadType,
    creationDate: item.creationDate,
    canDelete: item.uploaderGuid === user?.guid,
  }));

  const handleDelete = async (guid: string) => {
    try {
      await deleteActionFileEntry.mutateAsync({
        guid,
        actionSubmissionGuid,
      });
      notify("Evidence removed");
    } catch {
      notify("Failed to remove evidence", "error");
    }
  };

  return (
    <FileItemList
      files={files}
      onDelete={handleDelete}
      isDeletePending={deleteActionFileEntry.isPending}
    />
  );
}
